import React, { useState, useEffect } from 'react';
import { TERMS_OF_SERVICE, PRIVACY_POLICY, THIRD_PARTY_PROVISION, MARKETING_CONSENT } from '../constants/legalText';
import { getCommonPolicyByType } from '../services/api';

function TermsModal({ isOpen, onClose, initialTab = 'terms' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  // 이용약관/개인정보처리방침은 관리자가 등록한 내용(DB)을 사용한다.
  // 가입 시 동의하는 약관과 공개 페이지(/terms, /privacy)의 약관이 달라지지 않도록 출처를 하나로 맞춘 것.
  // 조회 실패 시에만 코드 상수로 폴백한다.
  const [dbContent, setDbContent] = useState({ terms: null, privacy: null });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    Promise.all([
      getCommonPolicyByType('TERMS').then(r => r.data?.content).catch(() => null),
      getCommonPolicyByType('PRIVACY').then(r => r.data?.content).catch(() => null),
    ]).then(([terms, privacy]) => {
      if (!cancelled) setDbContent({ terms: terms || null, privacy: privacy || null });
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  const tabs = [
    { id: 'terms', label: '서비스 이용약관', content: dbContent.terms || TERMS_OF_SERVICE, isHtml: !!dbContent.terms },
    { id: 'privacy', label: '개인정보처리방침', content: dbContent.privacy || PRIVACY_POLICY, isHtml: !!dbContent.privacy },
    { id: 'thirdParty', label: '제3자 제공 동의', content: THIRD_PARTY_PROVISION, isHtml: false },
    { id: 'marketing', label: '마케팅 정보 수신', content: MARKETING_CONSENT, isHtml: false },
  ];

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  const currentTab = tabs.find(t => t.id === activeTab);
  const currentContent = currentTab?.content || '';
  const currentTitle = currentTab?.label || '';
  const currentIsHtml = !!currentTab?.isHtml;

  return (
    <div className="relative z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        {/* Background Overlay */}
        <div 
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
            onClick={onClose}
        ></div>

        {/* Modal Position */}
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 md:p-10 text-center sm:p-0">
                
                {/* Modal Container */}
                <div className="relative flex h-[85vh] w-full max-w-[1000px] flex-col bg-white dark:bg-background-dark rounded-xl shadow-2xl overflow-hidden text-left transform transition-all">
                    
                    {/* Header */}
                    <header className="flex items-center justify-between border-b border-solid border-[#e7f3eb] dark:border-white/10 px-6 py-4 md:px-10 shrink-0 bg-white dark:bg-background-dark z-10">
                        <div className="flex items-center gap-4 text-[#0d1b12] dark:text-white">
                            <div className="w-6 h-6 text-primary-strong">
                                <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M24 4C25.7818 14.2173 33.7827 22.2182 44 24C33.7827 25.7818 25.7818 33.7827 24 44C22.2182 33.7827 14.2173 25.7818 4 24C14.2173 22.2182 22.2182 14.2173 24 4Z" fill="currentColor"></path>
                                </svg>
                            </div>
                            <h2 className="text-lg font-bold leading-tight tracking-[-0.015em]">이용약관 및 정책</h2>
                        </div>
                        <button 
                            onClick={onClose}
                            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#e7f3eb] dark:bg-white/10 text-[#0d1b12] dark:text-white hover:bg-red-50 hover:text-red-500 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </header>

                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar bg-[#f8fcf9] dark:bg-background-dark/50">
                        {/* Page Heading Section (Dynamic based on logic or static as per design?) 
                            The design has a big heading "이용약관 및 정책" but also tabs. 
                            I'll keep the design's "PageHeading" as a summary and then tabs.
                        */}
                        <div className="max-w-[960px] mx-auto w-full">
                            <div className="flex flex-col gap-3 p-6 md:p-10 pb-0">
                                <p className="text-[#0d1b12] dark:text-white tracking-light text-[28px] md:text-[32px] font-bold leading-tight">
                                    {currentTitle}
                                </p>
                                <p className="text-[#4c9a66] dark:text-primary/80 text-sm font-normal leading-normal">
                                    서비스 이용을 위해 아래 약관 내용을 확인해 주세요.
                                </p>
                            </div>

                            {/* Tabs Section */}
                            <div className="px-6 md:px-10 mt-6">
                                <div className="flex border-b border-[#cfe7d7] dark:border-white/10 gap-4 md:gap-8 overflow-x-auto">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex flex-col items-center justify-center pb-[13px] pt-4 whitespace-nowrap transition-colors border-b-[3px]
                                                ${activeTab === tab.id 
                                                    ? 'border-b-primary text-[#0d1b12] dark:text-white' 
                                                    : 'border-b-transparent text-[#4c9a66] dark:text-gray-400 hover:text-[#0d1b12] dark:hover:text-white'
                                                }`}
                                        >
                                            <p className="text-sm font-bold leading-normal tracking-[0.015em] px-1">
                                                {tab.label}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Legal Content Body */}
                            <div className="px-6 md:px-10 pb-20 pt-8">
                                {currentIsHtml ? (
                                    <div
                                        className="prose dark:prose-invert max-w-none text-[#0d1b12] dark:text-gray-300 text-base font-normal leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: currentContent }}
                                    />
                                ) : (
                                    <div className="prose dark:prose-invert max-w-none text-[#0d1b12] dark:text-gray-300 text-base font-normal leading-relaxed whitespace-pre-line">
                                        {currentContent}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <footer className="w-full border-t border-[#e7f3eb] dark:border-white/10 bg-white/80 dark:bg-background-dark/80 backdrop-blur-md px-6 py-6 md:px-10 flex flex-col md:flex-row items-center justify-end gap-4 shrink-0">
                        {/* 
                        Note: The design has a checkbox here. 
                        Since the state is managed in the parent (Signup.js), implementing a local checkbox here might be confusing 
                        unless we pass "onAgree" back. For now, we will just have the buttons interact with the parent's "Close".
                        */}
                        <div className="flex items-center gap-3 w-full md:w-auto">
                             <button 
                                onClick={onClose}
                                className="flex-1 md:flex-none px-8 py-3 rounded-lg bg-[#e7f3eb] dark:bg-white/10 text-[#0d1b12] dark:text-white font-bold text-sm hover:bg-[#d5e8db] transition-colors"
                            >
                                닫기
                            </button>
                            <button 
                                onClick={onClose}
                                className="flex-1 md:flex-none px-12 py-3 rounded-lg bg-primary text-primary-contrast font-bold text-sm shadow-lg shadow-primary/20 hover:brightness-105 active:scale-95 transition-all"
                            >
                                확인
                            </button>
                        </div>
                    </footer>
                </div>
            </div>
        </div>
        
        {/* Custom Scrollbar Styles */}
        <style>{`
            .custom-scrollbar::-webkit-scrollbar {
                width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
                background: transparent;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
                background: #cfe7d7;
                border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: #4c9a66;
            }
            .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                background: #2d4a3e;
            }
        `}</style>
    </div>
  );
}

export default TermsModal;
